/**
 * LegalPage — Wrapper public pour les pages légales (CGU, CGV, RGPD, Mentions).
 *
 * Design dark sobre cohérent avec la landing SaaS TripNova.
 * Pas d'authentification requise. SEO-friendly (title + meta description).
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function LegalPage({ title, subtitle, lastUpdate, children, testid }) {
  useEffect(() => {
    document.title = `${title} — TripNova SaaS`;
  }, [title]);

  return (
    <div className="min-h-screen bg-black text-zinc-200" data-testid={testid}>
      {/* Header minimal */}
      <header className="border-b border-white/5 bg-gradient-to-b from-zinc-950/80 to-black/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="legal-back-home">
            <div className="leading-tight">
              <div className="text-lg font-black">
                <span className="text-blue-400">TRIP</span><span className="text-orange-400">NOVA</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">CRM SaaS</div>
            </div>
          </Link>
          <Link
            to="/"
            className="text-xs text-zinc-400 hover:text-orange-400 transition"
            data-testid="legal-back-link"
          >
            ← Retour à l'accueil
          </Link>
        </div>
      </header>

      {/* Contenu */}
      <main className="max-w-4xl mx-auto px-6 py-10 md:py-16">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-black mb-2 text-white">{title}</h1>
          {subtitle && <p className="text-zinc-400 text-base">{subtitle}</p>}
          {lastUpdate && (
            <p className="text-xs text-zinc-500 mt-3">
              Dernière mise à jour : <span className="text-zinc-300">{lastUpdate}</span>
            </p>
          )}
        </div>

        <article className="prose prose-invert prose-zinc max-w-none
                            prose-headings:text-white prose-headings:font-black
                            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3 prose-h2:border-b prose-h2:border-white/10 prose-h2:pb-2
                            prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-2 prose-h3:text-orange-300
                            prose-p:text-zinc-300 prose-p:leading-relaxed
                            prose-strong:text-white
                            prose-a:text-orange-400 prose-a:no-underline hover:prose-a:underline
                            prose-ul:text-zinc-300 prose-li:my-1
                            prose-code:text-orange-300 prose-code:bg-white/5 prose-code:px-1 prose-code:rounded">
          {children}
        </article>

        {/* Footer nav */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap gap-x-6 gap-y-2 text-xs text-zinc-500">
          <Link to="/cgu" className="hover:text-orange-400 transition" data-testid="legal-link-cgu">CGU</Link>
          <Link to="/cgv" className="hover:text-orange-400 transition" data-testid="legal-link-cgv">CGV</Link>
          <Link to="/rgpd" className="hover:text-orange-400 transition" data-testid="legal-link-rgpd">Politique de confidentialité (RGPD)</Link>
          <Link to="/mentions-legales" className="hover:text-orange-400 transition" data-testid="legal-link-mentions">Mentions légales</Link>
          <span className="ml-auto">© {new Date().getFullYear()} TripNova SaaS</span>
        </div>
      </main>
    </div>
  );
}
